import { useRef, type Ref, type RefCallback } from 'react';

const assignRef = <T>(ref: Ref<T> | undefined, element: T | null) => {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(element);
  } else {
    (ref as { current: T | null }).current = element;
  }
};

export const useMergedRef = <T>(
  ...refs: (Ref<T> | undefined)[]
): RefCallback<T> => {
  const store = useRef<
    { refs: (Ref<T> | undefined)[]; merged: RefCallback<T> } | undefined
  >(undefined);

  // Return a new callback only when a ref changes identity. react-hook-form
  // hands a fresh register ref every render and relies on the callback being
  // re-invoked; a permanently-stable callback is only called once at mount, so
  // RHF loses the node and reset() can't write the input after mount. Stable
  // refs keep the same callback, so React doesn't detach/reattach each render.
  const prev = store.current;
  if (
    !prev ||
    prev.refs.length !== refs.length ||
    refs.some((ref, i) => ref !== prev.refs[i])
  ) {
    const merged: RefCallback<T> = (element) => {
      for (const ref of refs) {
        assignRef(ref, element);
      }
    };
    store.current = { refs, merged };
    return merged;
  }

  return prev.merged;
};
